import { useState } from 'react'
import {
  RELATIONSHIP_TYPES,
  DIRECTIONS,
  getRelationshipType,
  getDirection,
  getDirectionSymbol,
  getRelationshipColor
} from '../../lib/relationshipTypes'
import './RelationshipEditor.css'

const EMPTY_RELATIONSHIP = {
  targetId: '',
  type: 'companion',
  direction: 'to',
  notes: '',
  hidden: false
}

export default function RelationshipEditor({
  relationships = [],
  characters = [],
  currentCharacterId,
  onChange,
  onImportClick
}) {
  const [isAdding, setIsAdding] = useState(false)
  const [editIndex, setEditIndex] = useState(null)
  const [formData, setFormData] = useState(EMPTY_RELATIONSHIP)

  // Filter out current character from target options
  const targetOptions = characters.filter(c => c.id !== currentCharacterId)

  const getCharacterName = (id) => {
    const char = characters.find(c => c.id === id)
    return char?.name || 'Unknown Character'
  }

  const handleAdd = () => {
    setFormData(EMPTY_RELATIONSHIP)
    setIsAdding(true)
    setEditIndex(null)
  }

  const handleEdit = (index) => {
    setFormData({ ...relationships[index] })
    setEditIndex(index)
    setIsAdding(false)
  }

  const handleCancel = () => {
    setFormData(EMPTY_RELATIONSHIP)
    setIsAdding(false)
    setEditIndex(null)
  }

  const handleSave = () => {
    if (!formData.targetId) return

    // For 'self' direction, target must be a different identity of same person
    // We allow it but could add validation

    const newRelationships = [...relationships]

    if (editIndex !== null) {
      newRelationships[editIndex] = formData
    } else {
      // Check for duplicate
      const isDuplicate = relationships.some(
        r => r.targetId === formData.targetId &&
             r.type === formData.type &&
             r.direction === formData.direction
      )
      if (isDuplicate) {
        alert('This relationship already exists.')
        return
      }
      newRelationships.push(formData)
    }

    onChange(newRelationships)
    handleCancel()
  }

  const handleRemove = (index) => {
    const rel = relationships[index]
    const targetName = getCharacterName(rel.targetId)
    if (!window.confirm(`Remove relationship with ${targetName}?`)) return

    const newRelationships = relationships.filter((_, i) => i !== index)
    onChange(newRelationships)
  }

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const isEditing = isAdding || editIndex !== null

  return (
    <div className="relationship-editor">
      <div className="relationship-header">
        <label>Relationships</label>
        <div className="relationship-actions">
          {onImportClick && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={onImportClick}
            >
              Import CSV
            </button>
          )}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={handleAdd}
            disabled={isEditing}
          >
            + Add
          </button>
        </div>
      </div>

      {/* Relationship List */}
      <div className="relationship-list">
        {relationships.length === 0 && !isEditing && (
          <p className="no-relationships">No relationships defined.</p>
        )}

        {relationships.map((rel, index) => {
          const isEditingThis = editIndex === index
          if (isEditingThis) return null // Show form instead

          const type = getRelationshipType(rel.type)
          const direction = getDirection(rel.direction)

          return (
            <div
              key={index}
              className={`relationship-item ${rel.hidden ? 'is-hidden' : ''}`}
              style={{ '--rel-color': getRelationshipColor(rel.type) }}
            >
              <div className="relationship-content">
                <span className="relationship-direction" title={direction?.description}>
                  {getDirectionSymbol(rel.direction)}
                </span>
                <span className="relationship-target">
                  {getCharacterName(rel.targetId)}
                </span>
                <span
                  className="relationship-type-badge"
                  style={{ backgroundColor: getRelationshipColor(rel.type) }}
                >
                  {type?.label || rel.type}
                </span>
                {rel.hidden && (
                  <span className="relationship-hidden-badge" title="Hidden from players">
                    Hidden
                  </span>
                )}
              </div>
              {rel.notes && (
                <p className="relationship-notes">{rel.notes}</p>
              )}
              <div className="relationship-item-actions">
                <button
                  type="button"
                  className="btn-icon"
                  onClick={() => handleEdit(index)}
                  disabled={isEditing}
                  title="Edit"
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="btn-icon btn-danger"
                  onClick={() => handleRemove(index)}
                  disabled={isEditing}
                  title="Remove"
                >
                  &times;
                </button>
              </div>
            </div>
          )
        })}
      </div>

      {/* Add/Edit Form */}
      {isEditing && (
        <div className="relationship-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="rel-target">Target Character *</label>
              <select
                id="rel-target"
                value={formData.targetId}
                onChange={(e) => handleFormChange('targetId', e.target.value)}
                required
              >
                <option value="">Select character...</option>
                {targetOptions.map(char => (
                  <option key={char.id} value={char.id}>
                    {char.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="rel-type">Type</label>
              <select
                id="rel-type"
                value={formData.type}
                onChange={(e) => handleFormChange('type', e.target.value)}
              >
                {RELATIONSHIP_TYPES.map(type => (
                  <option key={type.id} value={type.id}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="rel-direction">Direction</label>
              <select
                id="rel-direction"
                value={formData.direction}
                onChange={(e) => handleFormChange('direction', e.target.value)}
              >
                {DIRECTIONS.map(dir => (
                  <option key={dir.id} value={dir.id}>
                    {dir.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="rel-notes">Notes (optional)</label>
            <input
              id="rel-notes"
              type="text"
              value={formData.notes}
              onChange={(e) => handleFormChange('notes', e.target.value)}
              placeholder="Additional context about this relationship..."
            />
          </div>

          <div className="form-group form-group-inline">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.hidden || false}
                onChange={(e) => handleFormChange('hidden', e.target.checked)}
              />
              <span>Hidden from players</span>
            </label>
            <span className="help-text">Hidden relationships won't appear on the public character page</span>
          </div>

          <div className="relationship-form-actions">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleSave}
              disabled={!formData.targetId}
            >
              {editIndex !== null ? 'Update' : 'Add'} Relationship
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
